/*
 * ============================================================
 * argile-navbar.js — JS de la section `argile-navbar` (header custom)
 * ============================================================
 * Convention Argile : 1 section = son fichier JS, chargé PAR la section
 * (cf. sections/argile-navbar.liquid), pas dans le bundle global.
 *
 * Fournit :
 *   - sticky header : classe `argile-navbar--scrolled` sur <header> au scroll
 *   - drawer mobile (hamburger) + accordéon mega-menu (caret)
 *   - état `is-mega-open` (fond solide quand un dropdown est ouvert)
 *   - header transparent (accueil) : mesure de hauteur → offset du contenu
 *   - éditeur : ouvre le mega-menu de l'item dont un bloc enfant est sélectionné
 *
 * Idempotent + ré-init sur `shopify:section:load` (éditeur de thème).
 */
(function () {
  'use strict';

  var stickyWired = false;

  /* Mesure la hauteur « repliée » du header en desktop : distance du haut du
     #header-group jusqu'au haut de la rangée d'onglets. En descendant, le groupe
     est translaté de cette valeur → seul le haut (annonce + logo/recherche/actions)
     disparaît, la barre des onglets reste épinglée en haut (comportement Hermès).
     Non mesurée pendant que le header est déjà replié (transform fausserait le
     rect) : on garde alors la dernière valeur connue. */
  function measureCollapse() {
    var group = document.getElementById('header-group');
    var nav = document.querySelector('.argile-navbar__nav');
    if (!group || !nav) return;
    if (group.classList.contains('argile-header--hidden')) return;
    var groupRect = group.getBoundingClientRect();
    var navRect = nav.getBoundingClientRect();
    var collapse = navRect.top - groupRect.top;
    if (collapse > 0) {
      document.documentElement.style.setProperty(
        '--argile-header-collapse',
        Math.round(collapse) + 'px'
      );
      // Hauteur de la bande d'onglets qui reste épinglée en haut après repli :
      // du haut de la rangée d'onglets jusqu'au bas du groupe. Sert à recaler les
      // éléments sticky en dessous (ex. nav des finitions).
      document.documentElement.style.setProperty(
        '--argile-header-pinned',
        Math.round(groupRect.bottom - navRect.top) + 'px'
      );
    }
  }

  /* Sticky : classe sur le <header> au-delà d'un seuil de scroll (transition CSS).
     + hide-on-scroll-down / show-on-scroll-up : on pose la classe `argile-header--hidden`
     sur le #header-group quand on descend, on la retire quand on remonte. Le CSS
     traduit ça en repli du haut (desktop, onglets conservés) ou en glissement
     complet à -100% (mobile / drawer). */
  function initStickyHeader() {
    if (stickyWired) return;
    stickyWired = true;

    var threshold = 24; // ombre « scrolled »
    var hideOffset = 128; // ~8rem : on ne masque qu'au-delà, pas au niveau du hero
    var lastY = window.scrollY;
    var ticking = false;

    measureCollapse();
    window.addEventListener('resize', measureCollapse);

    function update() {
      var header = document.querySelector('header');
      var group = document.getElementById('header-group');
      var y = window.scrollY;

      if (header) header.classList.toggle('argile-navbar--scrolled', y > threshold);

      if (group) {
        var goingDown = y > lastY;
        // On garde la nav visible si un mega-menu ou le drawer mobile est ouvert.
        var locked = header && (header.classList.contains('is-mega-open') ||
          header.classList.contains('is-nav-open'));
        if (goingDown && y > hideOffset && !locked) {
          group.classList.add('argile-header--hidden');
        } else if (!goingDown) {
          group.classList.remove('argile-header--hidden');
        }
        // Miroir global de l'état caché du header → permet à d'autres éléments
        // sticky (ex. nav des finitions) de suivre le même move au scroll.
        document.documentElement.classList.toggle(
          'argile-header-hidden',
          group.classList.contains('argile-header--hidden')
        );
      }

      lastY = y < 0 ? 0 : y;
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  function initNavbar(root) {
    var scope = root || document;
    var navbars = scope.querySelectorAll('.argile-navbar');
    if (!navbars.length) return;

    var mobileQuery = window.matchMedia('(max-width: 989px)');

    navbars.forEach(function (navbar) {
      if (navbar.dataset.argileWired) return;
      navbar.dataset.argileWired = '1';

      var burger = navbar.querySelector('.argile-navbar__burger');
      var nav = navbar.querySelector('.argile-navbar__nav');

      function closeDrawer() {
        navbar.classList.remove('is-nav-open');
        if (burger) burger.setAttribute('aria-expanded', 'false');
      }

      if (burger) {
        burger.addEventListener('click', function () {
          var open = navbar.classList.toggle('is-nav-open');
          burger.setAttribute('aria-expanded', String(open));
        });
      }

      // Croix de fermeture dans le drawer (mobile plein écran)
      var closeBtn = navbar.querySelector('.argile-navbar__close');
      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

      // Caret + libellé mega : accordéon (mobile uniquement ; desktop = survol CSS).
      // Le libellé d'un item mega est un <button> (pas un lien) → il ouvre/ferme
      // le panneau au lieu de naviguer.
      navbar.querySelectorAll('.argile-navbar__caret, .argile-navbar__link--toggle').forEach(function (toggle) {
        toggle.addEventListener('click', function (event) {
          if (!mobileQuery.matches) return;
          event.preventDefault();
          var item = toggle.closest('.argile-navbar__item');
          if (!item) return;
          var open = item.classList.toggle('is-open');
          // Synchronise aria-expanded sur le caret ET le libellé de l'item.
          item.querySelectorAll('.argile-navbar__caret, .argile-navbar__link--toggle').forEach(function (t) {
            t.setAttribute('aria-expanded', String(open));
          });
        });
      });

      // Clic extérieur → ferme le drawer
      document.addEventListener('click', function (event) {
        if (!navbar.classList.contains('is-nav-open')) return;
        if (nav && nav.contains(event.target)) return;
        if (burger && burger.contains(event.target)) return;
        closeDrawer();
      });

      // Échap → ferme le drawer
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeDrawer();
      });

      // Retour en desktop → reset drawer + accordéons
      mobileQuery.addEventListener('change', function (event) {
        if (event.matches) return;
        closeDrawer();
        navbar.querySelectorAll('.argile-navbar__item.is-open').forEach(function (item) {
          item.classList.remove('is-open');
        });
      });

      // Mesure la hauteur réelle de la navbar — exposée en --argile-navbar-height
      // pour TOUTES les pages (héros plein-écran qui soustraient la navbar, offset
      // d'ancres, etc.), pas seulement l'accueil transparent.
      var setNavHeight = function () {
        document.documentElement.style.setProperty('--argile-navbar-height', navbar.offsetHeight + 'px');
      };
      setNavHeight();
      window.addEventListener('resize', setNavHeight);

      // Header transparent (accueil) : remonte le contenu sous lui (cf. CSS).
      if (navbar.classList.contains('argile-navbar--transparent')) {
        document.body.classList.add('argile-has-transparent-header');
      }

      // État « mega-menu ouvert » → fond solide même sans scroll (sur l'accueil transparent).
      if (nav) {
        var megaTimer;
        // Dernier item mega ouvert : sert à distinguer un SWITCH direct (onglet →
        // onglet, un mega déjà ouvert) d'une PREMIÈRE ouverture depuis l'état fermé.
        var openItem = null;
        var currentItem = function () {
          return navbar.querySelector(
            '.argile-navbar__item--mega:hover, .argile-navbar__item--mega:focus-within, .argile-navbar__item.is-open'
          );
        };
        var syncMegaOpen = function () {
          var item = currentItem();
          navbar.classList.toggle('is-mega-open', !!item);
          if (item) {
            // Un autre mega était déjà ouvert → bascule instantanée (anti-flash).
            // Premier survol depuis l'état fermé → fondu fluide (classe absente).
            navbar.classList.toggle('is-mega-instant', !!openItem && openItem !== item);
            openItem = item;
          }
        };
        var openNow = function () {
          clearTimeout(megaTimer);
          syncMegaOpen();
        };
        // Retarde le retrait : passer d'un dropdown à l'autre ne fait pas clignoter le fond.
        var closeSoon = function () {
          clearTimeout(megaTimer);
          // La fermeture FINALE (curseur hors du menu) doit fondre : on retire le
          // flag instantané avant que le panneau ne se cache.
          navbar.classList.remove('is-mega-instant');
          megaTimer = setTimeout(function () {
            openItem = null;
            syncMegaOpen();
          }, 120);
        };
        nav.addEventListener('pointerover', openNow);
        nav.addEventListener('pointerout', closeSoon);
        nav.addEventListener('focusin', openNow);
        nav.addEventListener('focusout', closeSoon);
      }
    });
  }

  /* Éditeur de thème : ouvre le mega-menu de l'item dont un bloc enfant est sélectionné. */
  function navbarItemFromEvent(event) {
    var el = event.target;
    if (!el || el.nodeType !== 1) return null;
    return (el.closest && el.closest('.argile-navbar__item')) ||
      (el.querySelector && el.querySelector('.argile-navbar__item'));
  }

  function boot() {
    initStickyHeader();
    initNavbar(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initNavbar(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var item = navbarItemFromEvent(event);
    if (item) item.classList.add('is-open');
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var item = navbarItemFromEvent(event);
    if (item) item.classList.remove('is-open');
  });
})();
